import { useState, useCallback, useRef, useEffect } from "react";
import { Input } from "../components/ui/input";

/**
 * IntegerQuantityInput — Drop-in replacement for <Input type="number"> 
 * that enforces integer-only quantities.
 * 
 * - Blocks "." and "," at keypress level
 * - On paste: strips non-digits, shows inline error if decimal detected
 * - Shows inline validation: "Whole numbers only (no decimals)."
 * - Returns parsed integer via onChange(intValue)
 * - Allows 0 when allowZero=true (default true)
 * - Negative values allowed when allowNegative=true (default false)
 */
export function IntegerQuantityInput({
  value,
  onChange,
  min,
  max,
  placeholder = "0",
  disabled = false,
  className = "",
  allowZero = true,
  allowNegative = false,
  "data-testid": testId,
  ...rest
}) {
  const [error, setError] = useState("");
  const [displayValue, setDisplayValue] = useState(value?.toString() ?? "");
  const errorTimer = useRef(null);

  useEffect(() => {
    const raw = value?.toString() ?? "";
    // Always display as integer (strip ".000" from API values)
    const parsed = parseInt(raw, 10);
    const clean = isNaN(parsed) ? raw : parsed.toString();
    if (clean !== displayValue) {
      setDisplayValue(clean);
    }
  }, [value]);

  const clearError = useCallback(() => {
    if (errorTimer.current) clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setError(""), 3000);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "." || e.key === ",") {
      e.preventDefault();
      setError("Whole numbers only (no decimals).");
      clearError();
    }
  }, [clearError]);

  const handleChange = useCallback((e) => {
    let raw = e.target.value;

    // Allow empty for clearing
    if (raw === "" || (allowNegative && raw === "-")) {
      setDisplayValue(raw);
      setError("");
      onChange(raw === "" ? "" : raw);
      return;
    }

    // Strip anything that isn't a digit or leading minus
    const cleaned = allowNegative 
      ? raw.replace(/[^0-9-]/g, "").replace(/(?!^)-/g, "")
      : raw.replace(/[^0-9]/g, "");

    if (cleaned !== raw) {
      setError("Whole numbers only (no decimals).");
      clearError();
    }

    if (cleaned === "" || (allowNegative && cleaned === "-")) {
      setDisplayValue(cleaned);
      onChange(cleaned === "" ? "" : cleaned);
      return;
    }

    const parsed = parseInt(cleaned, 10);
    if (isNaN(parsed)) return;

    setDisplayValue(parsed.toString());
    setError("");
    onChange(parsed);
  }, [onChange, allowNegative, clearError]);

  const handlePaste = useCallback((e) => {
    const pasted = e.clipboardData.getData("text");
    if (pasted.includes(".") || pasted.includes(",")) {
      e.preventDefault();
      const intPart = pasted.split(/[.,]/)[0].replace(/[^0-9-]/g, "");
      if (intPart) {
        const parsed = parseInt(intPart, 10);
        if (!isNaN(parsed)) {
          setDisplayValue(parsed.toString());
          onChange(parsed);
        }
      }
      setError("Whole numbers only (no decimals).");
      clearError();
    }
  }, [onChange, clearError]);

  return (
    <div className="relative w-full">
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        min={min}
        max={max}
        placeholder={placeholder}
        disabled={disabled}
        className={`${className} ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
        data-testid={testId}
        {...rest}
      />
      {error && (
        <p 
          className="absolute -bottom-5 left-0 text-[11px] text-red-500 whitespace-nowrap"
          data-testid={testId ? `${testId}-error` : "qty-input-error"}
        >
          {error}
        </p>
      )}
    </div>
  );
}
